import { useState, useEffect, useCallback } from "react";
import { usePlaidLink } from "react-plaid-link";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { usePortfolio } from "@/context/PortfolioContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Landmark, RefreshCw, Unplug, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlaidLinkButton = ({ linkToken, onSuccess, loading }) => {
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (public_token, metadata) => {
      onSuccess(public_token, metadata);
    },
  });

  return (
    <Button
      data-testid="plaid-link-btn"
      onClick={() => open()}
      disabled={!ready || loading}
      className="gap-2"
      variant="outline"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Landmark className="w-4 h-4" />
      )}
      Connect Bank Account
    </Button>
  );
};

export const PlaidConnect = () => {
  const { user } = useAuth();
  const { refreshAll } = usePortfolio();
  const { t } = useI18n();
  const [linkToken, setLinkToken] = useState(null);
  const [plaidItems, setPlaidItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [showManage, setShowManage] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/plaid/items`, { withCredentials: true });
      setPlaidItems(resp.data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (user?.plaid_enabled) {
      fetchItems();
    }
  }, [user, fetchItems]);

  const createLinkToken = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/api/plaid/create-link-token`, {}, { withCredentials: true });
      setLinkToken(resp.data.link_token);
    } catch (err) {
      toast.error("Failed to initialize bank connection");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = async (publicToken, metadata) => {
    setLoading(true);
    try {
      const institutionName = metadata?.institution?.name || "Bank";
      await axios.post(
        `${API_URL}/api/plaid/exchange-token`,
        { public_token: publicToken, institution_name: institutionName },
        { withCredentials: true }
      );
      toast.success(`Connected to ${institutionName}! Data synced.`);
      await fetchItems();
      await refreshAll();
      setLinkToken(null);
    } catch (err) {
      toast.error("Failed to link bank account");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (itemId) => {
    setSyncing(itemId);
    try {
      await axios.post(`${API_URL}/api/plaid/sync/${itemId}`, {}, { withCredentials: true });
      toast.success("Data synced successfully");
      await refreshAll();
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(null);
    }
  };

  const handleDisconnect = async (itemId, name) => {
    try {
      await axios.delete(`${API_URL}/api/plaid/items/${itemId}`, { withCredentials: true });
      toast.success(`Disconnected ${name}`);
      await fetchItems();
      await refreshAll();
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const handleSandboxConnect = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/api/plaid/sandbox-connect`, {}, { withCredentials: true });
      toast.success(`Connected to ${resp.data.institution}! Synced ${resp.data.synced.balances} accounts, ${resp.data.synced.investments} holdings, ${resp.data.synced.liabilities} liabilities.`);
      await fetchItems();
      await refreshAll();
    } catch (err) {
      toast.error("Sandbox connect failed");
    } finally {
      setLoading(false);
    }
  };

  if (!user?.plaid_enabled) return null;

  return (
    <>
      <div className="flex items-center gap-2">
        {plaidItems.length === 0 ? (
          <>
            {linkToken ? (
              <PlaidLinkButton linkToken={linkToken} onSuccess={handleSuccess} loading={loading} />
            ) : (
              <>
                <Button
                  data-testid="plaid-connect-btn"
                  onClick={createLinkToken}
                  disabled={loading}
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs border-primary/40 hover:border-primary"
                >
                  {loading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Landmark className="w-3.5 h-3.5" />
                  )}
                  {t("plaid.connectBank") || "Connect Bank"}
                </Button>
                <Button
                  data-testid="plaid-sandbox-btn"
                  onClick={handleSandboxConnect}
                  disabled={loading}
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-[10px] text-muted-foreground"
                >
                  {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Sandbox Test
                </Button>
              </>
            )}
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowManage(true)}
              className="gap-1.5 text-xs"
              data-testid="plaid-manage-btn"
            >
              <Landmark className="w-3.5 h-3.5" />
              {plaidItems.length} Connected
            </Button>
          </>
        )}
      </div>

      {/* Manage Connected Banks Dialog */}
      <Dialog open={showManage} onOpenChange={setShowManage}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5" />
              {t("plaid.connectedBanks") || "Connected Banks"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {plaidItems.map((item) => (
              <div
                key={item.item_id}
                className="flex items-center justify-between p-3 rounded-lg bg-accent/30 border border-border"
                data-testid={`plaid-item-${item.item_id}`}
              >
                <div>
                  <p className="font-medium text-sm">{item.institution_name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {item.last_synced
                      ? `Last synced: ${new Date(item.last_synced).toLocaleDateString()}`
                      : `Linked: ${new Date(item.linked_at).toLocaleDateString()}`}
                  </p>
                  {item.sync_summary && (
                    <div className="flex gap-1.5 mt-1">
                      {item.sync_summary.balances > 0 && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                          {item.sync_summary.balances} accounts
                        </Badge>
                      )}
                      {item.sync_summary.investments > 0 && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                          {item.sync_summary.investments} holdings
                        </Badge>
                      )}
                      {item.sync_summary.liabilities > 0 && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                          {item.sync_summary.liabilities} liabilities
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleSync(item.item_id)}
                    disabled={syncing === item.item_id}
                    data-testid={`plaid-sync-${item.item_id}`}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${syncing === item.item_id ? "animate-spin" : ""}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => handleDisconnect(item.item_id, item.institution_name)}
                    data-testid={`plaid-disconnect-${item.item_id}`}
                  >
                    <Unplug className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {plaidItems.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No banks connected yet.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PlaidConnect;
